import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Edit, CheckCircle, XCircle, Play, Square } from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import historyService from '../../../../../services/historyService';

export default function ProcessTaskLogsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProcessTaskLogDetail();
  }, [id]);

  const fetchProcessTaskLogDetail = async () => {
    setLoading(true);
    try {
      const result = await historyService.getProcessTaskHistoryDetail(id, authTokens.access);
      if (result.success) {
        setLog(result.data);
      } else {
        showError(`Erreur: ${result.error}`);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      showError("Impossible de charger le détail de l'historique");
      setTimeout(() => navigate(-1), 2000);
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.map(item => renderValue(item)).join(', ')}]`;
      }
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <p className="text-sm text-gray-500">Aucun changement détaillé enregistré.</p>;
    }
    
    return (
      <div className="space-y-3">
        {Object.entries(changes).map(([key, value]) => (
          <div key={key} className="border-l-4 border-blue-200 pl-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {key.replace(/_/g, ' ')}:
                </span>
                <div className="mt-1 text-sm text-gray-900">
                  {renderValue(value)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getActionIcon = (actionType) => {
    const iconConfig = {
      'create': { icon: Edit, color: 'text-green-600' },
      'update': { icon: Edit, color: 'text-blue-600' },
      'delete': { icon: XCircle, color: 'text-red-600' },
      'accept': { icon: CheckCircle, color: 'text-green-600' },
      'reject': { icon: XCircle, color: 'text-red-600' },
      'start': { icon: Play, color: 'text-blue-600' },
      'complete': { icon: CheckCircle, color: 'text-purple-600' },
      'status_change': { icon: Edit, color: 'text-yellow-600' },
      'assign': { icon: User, color: 'text-indigo-600' }
    };

    const config = iconConfig[actionType] || { icon: Edit, color: 'text-gray-600' };
    const IconComponent = config.icon;
    
    return <IconComponent className={`h-5 w-5 ${config.color}`} />;
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'create': 'Création',
      'update': 'Modification',
      'delete': 'Suppression',
      'accept': 'Acceptée',
      'reject': 'Rejetée',
      'start': 'Démarrée',
      'complete': 'Terminée',
      'status_change': 'Changement de statut',
      'assign': 'Assignée'
    };
    return labels[actionType] || actionType;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Détail de l'historique non trouvé.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Détail de l'Historique - Tâche de Processus</h1>
          <p className="text-sm text-gray-500">Informations détaillées sur l'action effectuée</p>
        </div>
      </div>

      {/* Main Info Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            {getActionIcon(log.action_type)}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {getActionLabel(log.action_type)} - {log.process_task_name}
              </h2>
              <span className="text-sm text-gray-500">
                ID: {log.id}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{log.description}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Task Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Informations de la Tâche</h3>
            <div className="space-y-2">
              <div className="flex items-center text-sm">
                <Square className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Tâche:</span>
                <span className="ml-2 font-medium">{log.process_task_name}</span>
              </div>
              <div className="flex items-center text-sm">
                <Square className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Processus:</span>
                <span className="ml-2 font-medium">{log.process_name}</span>
              </div>
              <div className="flex items-center text-sm">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Assignée à:</span>
                <span className="ml-2 font-medium">{log.assigned_to_email}</span>
              </div>
            </div>
          </div>

          {/* Action Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Informations de l'Action</h3>
            <div className="space-y-2">
              <div className="flex items-center text-sm">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Effectuée par:</span>
                <span className="ml-2 font-medium">{log.done_by_email || 'Système'}</span>
              </div>
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Date:</span>
                <span className="ml-2 font-medium">
                  {new Date(log.action_date).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex items-center text-sm">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Heure:</span>
                <span className="ml-2 font-medium">
                  {new Date(log.action_date).toLocaleTimeString('fr-FR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Changes Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails des Changements</h3>
        {renderChanges(log.changes_summary)}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button 
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Retour
        </button>
      </div>
    </div>
  );
}
